import { IAccountSettingsForm } from 'src/lib/schemas/account/settings';
import http from './http';
import { processCallback } from 'src/lib/utils/processCallback';
import { ConditionsResponse } from 'src/lib/schemas/account/conditions';
import {
	IAvailableChannels,
	ICompanyAccount,
	ICompanyAccountForm,
	ICompanyAccountUpdate,
	ICustomerProfile,
} from 'src/lib/schemas';

const apiVersion = 'v1';
const accountUrl = `/${apiVersion}/account`;

const updateCompanyAccount = async (
	payload: Partial<
		ICompanyAccountForm | ICompanyAccountUpdate | IAccountSettingsForm
	>,
): Promise<ICompanyAccount> => {
	const { account, ...rest } = payload as any;
	if ('id' in rest) {
		delete rest.id;
	}
	const { data } = await http.patch<ICompanyAccount>(
		`${accountUrl}/id/${account}`,
		{ ...rest },
	);
	return data;
};

const getCompanyAccount = async (
	accountId: string,
): Promise<ICompanyAccount> => {
	const { data } = await http.get<ICompanyAccount>(
		`${accountUrl}/id/${accountId}`,
	);
	return data;
};

const getAvailableChannels = async (
	accountId?: string,
): Promise<IAvailableChannels> => {
	const { data } = await http.get<IAvailableChannels>(
		`${accountUrl}/id/${accountId}/available_channels`,
	);
	return data;
};

const getAccountConfigOptions = async <T>(configName: string): Promise<T[]> => {
	try {
		const { data } = await http.get(`${accountUrl}/${configName}`);
		return data;
	} catch (error) {
		return [];
	}
};

const createAccountConfigOptions = async <T>(
	configName: string,
	payload: T,
) => {
	const { data } = await http.post(`${accountUrl}/${configName}`, payload);
	return data;
};

const updateAccountConfigOptions = async <T extends { id?: string }>(
	configName: string,
	payload: T,
) => {
	const { id, ...rest } = payload;
	const { data } = await http.patch(`${accountUrl}/${configName}/${id}`, rest);
	return data;
};

const deleteAccountConfigOptions = async (configName: string, id: string) => {
	await http.delete(`${accountUrl}/${configName}/${id}`);
};

const uploadFontFile = async (file: File) => {
	const formData = new FormData();
	formData.append('fontFile', file);

	const { data } = await http.post(`${accountUrl}/font-families`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});

	return data;
};

const getServiceableAreas = async (): Promise<any[]> => {
	const url = `/${apiVersion}/account/serviceable-areas/`;
	const { data } = await http.get(url);
	// If the API returns { data: [...] }, return data.data; otherwise, return data
	return Array.isArray(data) ? data : data.data || [];
};

// Location API for campaigns
export interface ICreateLocationPayload {
	name: string;
	address: {
		street: string;
		city: string;
		state: string;
		postalCode: string;
		country: string;
		lat: number;
		long: number;
		placeId?: string;
		regionCode?: string;
	};
	isDefault?: boolean;
	phone?: string;
}

const getLocations = async (): Promise<any[]> => {
	const url = `/${apiVersion}/account/locations`;
	const { data } = await http.get(url);
	return Array.isArray(data) ? data : data.data || [];
};

const createLocation = async (payload: ICreateLocationPayload): Promise<any> => {
	const url = `/${apiVersion}/account/locations`;
	const { data } = await http.post(url, payload);
	return data;
};

const updateLocation = async (id: string, payload: Partial<ICreateLocationPayload>): Promise<any> => {
	const url = `/${apiVersion}/account/locations/${id}`;
	const { data } = await http.patch(url, payload);
	return data;
};

const deleteLocation = async (id: string): Promise<void> => {
	const url = `/${apiVersion}/account/locations/${id}`;
	await http.delete(url);
};

const createServiceableArea = async (payload: any): Promise<any> => {
	const url = `/${apiVersion}/account/serviceable-areas/`;
	const { data } = await http.post(url, payload);
	return data;
};

const updateServiceableArea = async (
	id: string,
	payload: any,
): Promise<any> => {
	const url = `/${apiVersion}/account/serviceable-areas/${id}`;
	const { data } = await http.patch(url, payload);
	return data;
};

const deleteServiceableArea = async (id: string): Promise<void> => {
	const url = `/${apiVersion}/account/serviceable-areas/${id}`;
	await http.delete(url);
};

const getCustomerProfiles = async (): Promise<ICustomerProfile[]> => {
	const { data } = await http.get<ICustomerProfile[]>(
		`${accountUrl}/customer-profiles`,
	);

	return data;
};

const createCustomerProfile = async (
	payload: Omit<ICustomerProfile, '_id' | '__v' | 'account'>,
): Promise<ICustomerProfile> => {
	const { data } = await http.post<ICustomerProfile>(
		`${accountUrl}/customer-profiles`,
		payload,
	);
	return data;
};

const updateCustomerProfile = async (
	id: string,
	payload: Partial<Omit<ICustomerProfile, '_id' | '__v' | 'account'>>,
): Promise<ICustomerProfile> => {
	const { data } = await http.put<ICustomerProfile>(
		`${accountUrl}/customer-profiles/${id}`,
		payload,
	);
	return data;
};

const deleteCustomerProfile = async (id: string): Promise<void> => {
	await http.delete(`${accountUrl}/customer-profiles/${id}`);
};

// Custom polling function for customer profile generation with shorter intervals
const pollCustomerProfileCallback = async (
	callbackUrl: string,
	maxRetries: number = 60,
	delayMs: number = 500,
): Promise<any> => {
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			const response = await http.get(callbackUrl);
			const { status, body } = response.data;

			console.log(`[generateCustomerProfile] Poll attempt ${attempt + 1}:`, { status, hasBody: !!body, bodyKeys: body ? Object.keys(body) : [] });

			// Check if still processing
			if (status === 'pending' || status === 'processing' || status === 'partialContent') {
				// If partialContent with empty body, keep waiting
				if (status === 'partialContent' && (!body || Object.keys(body).length === 0)) {
					await new Promise(resolve => setTimeout(resolve, delayMs));
					continue;
				}
				// If partialContent with some data, keep waiting for completion
				if (status === 'partialContent') {
					await new Promise(resolve => setTimeout(resolve, delayMs));
					continue;
				}
				await new Promise(resolve => setTimeout(resolve, delayMs));
				continue;
			}

			// Check for successful completion
			if (status === 'successful') {
				console.log('[generateCustomerProfile] Successful response body:', JSON.stringify(body, null, 2));
				return body;
			}

			// Any other status, return what we have
			return body || { status, error: 'Unknown status' };
		} catch (error) {
			console.error(`[generateCustomerProfile] Poll error at attempt ${attempt + 1}:`, error);
			if (attempt < maxRetries - 1) {
				await new Promise(resolve => setTimeout(resolve, delayMs));
			}
		}
	}

	// Max retries reached
	console.error('[generateCustomerProfile] Max retries reached');
	return { status: 'timeout', maxAttemptsReached: true };
};

const generateCustomerProfile = async (
	name: string,
	description: string,
): Promise<ICustomerProfile> => {
	const body = {
		inputs: {
			variables: {
				name,
				description,
			},
		},
	};

	console.log('[generateCustomerProfile] Calling API with:', { name, description });

	const { data } = await http.post(
		'/v2/apps/fusion_ai.generate_customer_profiles/execute/generate_customer_profiles',
		body,
	);

	if (!data.callback) {
		throw new Error('No callback URL returned from API');
	}

	console.log('[generateCustomerProfile] Got callback URL:', data.callback);

	// Poll the callback for the result with shorter intervals
	const result = await pollCustomerProfileCallback(data.callback, 60, 500); // 60 attempts x 500ms = 30 seconds max

	console.log('[generateCustomerProfile] Callback result:', {
		hasOutput: !!result?.output,
		keys: result ? Object.keys(result) : [],
		outputKeys: result?.output ? Object.keys(result.output) : [],
	});

	// Extract profile data from result
	let profileData = result?.output || result;

	// If output is an array, extract first element
	if (Array.isArray(profileData) && profileData.length > 0) {
		profileData = profileData[0];
	}

	// Handle nested callback - if the result has a callback URL, we need to fetch from it
	if (profileData?.callback && !profileData.name) {
		console.log('[generateCustomerProfile] Polling nested callback:', profileData.callback);
		const nestedResult = await pollCustomerProfileCallback(profileData.callback, 60, 500);
		profileData = nestedResult?.output || nestedResult;

		// If nested result is an array, extract first element
		if (Array.isArray(profileData) && profileData.length > 0) {
			profileData = profileData[0];
		}

		console.log('[generateCustomerProfile] Nested result:', {
			hasName: !!profileData?.name,
			hasRules: !!profileData?.customer_rules,
			hasTones: !!profileData?.customer_tones,
			keys: profileData ? Object.keys(profileData) : []
		});
	}

	if (!profileData || !profileData.name) {
		console.error('[generateCustomerProfile] Invalid profile data:', profileData);
		throw new Error('Failed to generate customer profile - no valid data returned');
	}

	console.log('[generateCustomerProfile] Final profile:', {
		name: profileData.name,
		hasRules: !!profileData.customer_rules?.length,
		hasTones: !!profileData.customer_tones?.length,
		hasKeywords: !!profileData.customer_keywords?.length,
	});

	return profileData as ICustomerProfile;
};

const getConditions = async (): Promise<ConditionsResponse> => {
	const { data } = await http.get<ConditionsResponse>('/v1/conditions');
	return data;
};

interface AiVoiceAgentResponse {
	success: boolean;
	message: string;
	missingFields?: string[];
	status?: 'disabled' | 'configuring' | 'active';
}

const activateAiVoiceAgent = async (email: string): Promise<AiVoiceAgentResponse> => {
	const { data } = await http.post<AiVoiceAgentResponse>(
		`${accountUrl}/ai-voice-agent/activate`,
		{ email },
	);
	return data;
};

const deactivateAiVoiceAgent = async (): Promise<AiVoiceAgentResponse> => {
	const { data } = await http.post<AiVoiceAgentResponse>(
		`${accountUrl}/ai-voice-agent/deactivate`,
	);
	return data;
};

export {
	getCompanyAccount,
	updateCompanyAccount,
	getAvailableChannels,
	getAccountConfigOptions,
	createAccountConfigOptions,
	updateAccountConfigOptions,
	deleteAccountConfigOptions,
	uploadFontFile,
	getServiceableAreas,
	createServiceableArea,
	updateServiceableArea,
	deleteServiceableArea,
	getLocations,
	createLocation,
	updateLocation,
	deleteLocation,
	getCustomerProfiles,
	createCustomerProfile,
	updateCustomerProfile,
	deleteCustomerProfile,
	generateCustomerProfile,
	getConditions,
	activateAiVoiceAgent,
	deactivateAiVoiceAgent,
};
